/**
 * ブラウザのスタックトレースの解析と、画面に出すための整形（#456）。
 *
 * @remarks
 * Chrome（`    at fn (url:行:列)`）と Firefox・Safari（`fn@url:行:列`）の書式を読む。
 * 本番のスタックは圧縮後の位置なので、`lib/errors/source-map.ts` がソースマップで元の
 * ソースの位置（`src/components/…tsx:行:列`）に戻す。
 */

/** スタックの 1 フレーム。 */
export interface StackFrame {
  /** 関数名（無名・読めないときは null）。 */
  functionName: string | null;
  /** スクリプトの URL。 */
  file: string;
  /** 行（1 始まり）。 */
  line: number;
  /** 列（1 始まり）。 */
  column: number;
}

const CHROME_FRAME = /^\s*at (?:(.+?) \()?(.+?):(\d+):(\d+)\)?\s*$/;
const FIREFOX_FRAME = /^\s*(?:(.*?)@)?(.+?):(\d+):(\d+)\s*$/;

/** 画面に出すスタックの行数の上限（元の位置に戻せなかったとき）。全文はコピーとログに入る。 */
export const MAX_DISPLAY_FRAMES = 15;

/**
 * スタックの 1 行をフレームとして読む（フレームでない行は null）。
 *
 * @param line - スタックの 1 行。
 */
export function parseStackLine(line: string): StackFrame | null {
  const isChromeStyle = /^\s*at /.test(line);
  const match = (isChromeStyle ? CHROME_FRAME : FIREFOX_FRAME).exec(line);
  if (match == null) return null;
  const [, fn, file, lineNo, column] = match;
  if (!file || !/^[a-z][\w+.-]*:/i.test(file)) return null;
  return {
    functionName: fn && fn.trim() !== "" ? fn.trim() : null,
    file,
    line: Number(lineNo),
    column: Number(column),
  };
}

/**
 * ソースマップの `sources` の書き方を、リポジトリの FE からの相対パスにそろえる。
 *
 * - Turbopack: `turbopack:///[project]/apps/fe/src/x.tsx` → `src/x.tsx`
 * - webpack: `webpack://_N_E/./src/x.tsx` → `src/x.tsx`
 * - 依存: `…/node_modules/react-dom/…` → `node_modules/react-dom/…`
 *
 * @param source - ソースマップの `sources` の 1 つ（URL に解決済みでもよい）。
 */
export function normalizeSourcePath(source: string): string {
  let path = decodeURI(source);
  const nodeModules = path.lastIndexOf("node_modules/");
  if (nodeModules >= 0) return path.slice(nodeModules);
  path = path
    .replace(/^turbopack:\/\/\/(\[project\]\/)?/, "")
    .replace(/^webpack:\/\/[^/]*\//, "")
    .replace(/^\.\//, "");
  const appFe = path.indexOf("apps/fe/");
  if (appFe >= 0) path = path.slice(appFe + "apps/fe/".length);
  const src = path.indexOf("/src/");
  if (!path.startsWith("src/") && src >= 0) path = path.slice(src + 1);
  return path;
}

/**
 * スタックの行がアプリのソース（`src/…`）か。
 *
 * @param line - 元の位置に戻したスタックの 1 行。
 */
export function isAppFrameLine(line: string): boolean {
  return /(^|[\s(])src\//.test(line) && !line.includes("node_modules/");
}

/**
 * 画面に出すスタックの行（ダイアログをスクリーンショット 1 枚に収めるための整形）。
 *
 * - 先頭の「型: 説明」の行は落とす（ダイアログの別の欄に出している）
 * - 元の位置に戻せたスタック: アプリのソースのフレームは全部出し、ライブラリ内部が続くところは
 *   「…（ライブラリ内部 n 行）」の 1 行にまとめる
 * - 戻せなかったスタック（圧縮後の位置）: 先頭 {@link MAX_DISPLAY_FRAMES} 行と残りの行数
 *
 * @param stack - スタック（`error.stack`）。
 * @param resolved - 元の位置に戻したスタックか。
 */
export function displayStackLines(stack: string, resolved: boolean): string[] {
  const lines = stack.split("\n").map((l) => l.trimEnd());
  const first = lines.findIndex((l) => /^\s*at /.test(l) || parseStackLine(l) != null);
  const frames = (first >= 0 ? lines.slice(first) : lines).filter((l) => l.trim() !== "");
  if (resolved && frames.some(isAppFrameLine)) {
    const out: string[] = [];
    let skipped = 0;
    for (const line of frames) {
      if (isAppFrameLine(line)) {
        if (skipped > 0) out.push(`    …（ライブラリ内部 ${skipped} 行）`);
        skipped = 0;
        out.push(line);
      } else {
        skipped += 1;
      }
    }
    if (skipped > 0) out.push(`    …（ライブラリ内部 ${skipped} 行）`);
    return out;
  }
  if (frames.length <= MAX_DISPLAY_FRAMES) return frames;
  return [
    ...frames.slice(0, MAX_DISPLAY_FRAMES),
    `    …ほか ${frames.length - MAX_DISPLAY_FRAMES} 行（「すべてコピー」には全文が入ります）`,
  ];
}
